import { api } from '@verifime/api-definition';
import { IdentityFilter, TCustomerIdentity } from '@verifime/components';
import { useCallback, useState } from 'react';
import { TNotificationHandler } from '../../types';

/**
 * Custom hook to manage customer lookup using the v2 endpoint.
 * Provides search functionality with filtering, pagination, and sorting.
 */
export function useCustomerLookupData({
  isLegacyMode = true,
  pageSize,
  errorNotificationHandler = console.error,
}: Readonly<{
  isLegacyMode: boolean;
  pageSize: number;
  errorNotificationHandler?: TNotificationHandler;
}>) {
  const [hasSearched, setHasSearched] = useState(false);
  const [customers, setCustomers] = useState<TCustomerIdentity[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  /**
   * Fetch customers from the v2 endpoint based on search criteria and pagination.
   */
  const fetchCustomers = useCallback(
    (searchText: string, filters: IdentityFilter) => {
      setIsLoading(true);
      setError(null);

      const freeTextCriteria = isLegacyMode
        ? {
            name: searchText,
          }
        : {
            criteria: searchText,
          };

      return api
        .getV2customer({
          queries: {
            ...freeTextCriteria,

            // we will support pagination later
            // we will hard code these for now.
            beginIndex: 0,
            endIndex: pageSize, // exclusive - due to 0 based index

            orderBy: 'createdDate',
            orderDirection: 'desc',

            // TODO: our defined types are completely broken and not aligned with API's expectation
            // this will need to be fixed.
            type: filters.type as any,
            status: filters.status as any,
          },
        })
        .then((response) => {
          setCustomers(response.customers as TCustomerIdentity[]);
        })
        .catch((error) => {
          setError(error);
          errorNotificationHandler('Failed to load customers. Please try again.');
          setCustomers([]);
        })
        .finally(() => {
          setIsLoading(false);
        });
    },
    [errorNotificationHandler, pageSize],
  );

  /**
   * Handle search submission
   */
  const handleSearch = useCallback(
    async (searchText: string, filters: IdentityFilter) => {
      await fetchCustomers(searchText, filters);
      setHasSearched(true);
    },
    [fetchCustomers],
  );

  const reset = () => {
    setCustomers([]);
    setIsLoading(false);
    setError(null);
    setHasSearched(false);
  };

  return {
    customers,
    isLoading,
    error,
    hasSearched,
    reset,
    handleSearch,
  };
}
